import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, ThemeProvider, createTheme } from '@mui/material';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AuthProvider } from './contexts/AuthContext';
import HomeHeader from './components/headers/HomeHeader';
import LoginHeader from './components/headers/LoginHeader';
import AccountHeader from './components/headers/AccountHeader';
import RegistrationHeader from './components/headers/RegistrationHeader';
import ApplicationHeader from './components/headers/ApplicationHeader';
import CalibrationHeader from './components/headers/CalibrationHeader';
import MedicalHeader from './components/headers/MedicalHeader';
import DoHeader from './components/headers/DoHeader';
import JdHeader from './components/headers/JdHeader';
import AssessorHeader from './components/headers/AssessorHeader';
import AdminHeader from './components/headers/AdminHeader';
import DashboardHeader from './components/headers/DashboardHeader';
import AppRouter from './routes/AppRouter';
import TestingHeader from './components/headers/TestingHeader';
import SessionTimeoutWarning from './components/SessionTimeoutWarning';
import ConvenerHeader from './components/headers/ConvenerHeader';
import CEOHeader from './components/headers/CEOHeader';
import DirectorHeader from './components/headers/DirectorHeader';
import ComplaintHeader from './components/headers/ComplaintHeader';
import CertificateCellHeader from './components/headers/CertificateCellHeader';
import useAutoLogout from './utils/useAutoLogout';


// Create a custom theme with NABL colors
const theme = createTheme({
  palette: {
    primary: {
      main: '#00024c',
      light: '#1a2b6b',
      dark: '#000235',
    },
    secondary: {
      main: '#E34124',
      light: '#ff6f4e',
      dark: '#a02d19',
    },
    background: {
      default: '#fafafa',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    h3: {
      fontWeight: 600,
    },
    h4: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)',
          border: '1px solid #e0e0e0',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          borderRadius: 8,
        },
        contained: {
          background: 'linear-gradient(135deg, #00024c 0%, #1a2b6b 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #1a2b6b 0%, #00024c 100%)',
            transform: 'translateY(-1px)',
            boxShadow: '0 8px 25px rgba(0, 2, 76, 0.3)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#1a2b6b',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#00024c',
              borderWidth: 2,
            },
          },
        },
      },
    },
  },
});

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

function AppContent() {
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname + location.search;
    if (!path.startsWith('/main/login') && !path.startsWith('/login') && path !== '/') {
      localStorage.setItem('lastVisitedPage', path);
    }
  }, [location]);

  // Function to render the appropriate header based on the current route
  const renderHeader = () => {
    const path = location.pathname;
    
    // Check for specific application type patterns
    if (path.startsWith('/main/calibration')) {
      return <CalibrationHeader />;
    }
    
    if (path.startsWith('/main/medical')) {
      return <MedicalHeader />;
    }
    if (path.startsWith('/main/do')) {
      return <DoHeader />;
    }
    if (path.includes('/additional-documents')) {
      return <DoHeader />;
    }
    if (path.startsWith('/main/jd')) {
      return <JdHeader />;
    }
    if (path.startsWith('/main/assessor')) {
      return <AssessorHeader />;
    }
    if (path.startsWith('/main/admin')) {
      return <AdminHeader />;
    }
    if (path.startsWith('/main/view')) {
      return <AdminHeader />;
    }
    if (path.startsWith('/main/superAdmin')) {
      return <AdminHeader />;
    }
    
    if (path.startsWith('/main/testing')) {
      return <TestingHeader />;
    }
    
    if (path.startsWith('/main/accounts')) {
      return <AccountHeader />;
    }

    if (path.startsWith('/main/convener')) {
      return <ConvenerHeader />;
    }

    if (path.startsWith('/main/ceo')) {
      return <CEOHeader />;
    }

    if (path.startsWith('/main/director')) {
      return <DirectorHeader />;
    }

    if (path.startsWith('/main/complaint')) {
      return <ComplaintHeader />;
    }

    if (path.startsWith('/main/certificate-cell')) {
      return <CertificateCellHeader />;
    }

    if (path.includes('/main/unpaid-assessment-proforma-invoice')) {
      return null;
    }
    
    // Check for other application form routes that should use ApplicationHeader
    if (path.includes('-application') || 
        path === '/main/application-form' ||
        path === '/main/laboratory-details' ||
        path === '/main/lab-details' ||
        path === '/main/organization' ||
        path === '/main/proficiency-testing' ||
        path === '/main/reference-standards' ||
        path === '/main/measurement-uncertainty' ||
        path === '/main/lab-staff' ||
        path === '/main/management-system' ||
        path === '/main/fees' ||
        path === '/main/laboratory/feedback' ||
        path === '/main/laboratory/form217-preview' ||
        path === '/main/laboratory/nc-report' ||
        path === '/main/laboratory/nc-application-finding' ||
        path === '/main/laboratory/form9' ||
        path === '/main/laboratory/form8' ||
        path === '/main/laboratory/advisory' ||
        path === '/main/assessment-fees-payment' ||
        path === '/main/form72' ||
        path === '/main/laboratory/form73-calibration' ||
        path === '/main/enclosure') {
      return <ApplicationHeader />;
    }
    // Handle specific page routes
    switch (path) {
      case '/main/login':
        return <LoginHeader />;
      case '/main/register':
      case '/main/register-temp':
      case '/main/register-ppp-ffl':
        return <RegistrationHeader />;
      case '/main/dashboard':
      case '/main/outstanding-ledger':
      case '/main/ledger':
      case '/main/choose-assessment-date':
      case '/main/assessment-details':
      case '/main/assessor-profile':
      case '/main/laboratory-update-details':
        case '/main/cab-accept-assessors':
        case '/main/laboratory/scope-witness':
        case '/main/laboratory/scope-witness-testing':
        case '/main/laboratory/scope-witness-calibration':
        case '/main/laboratory/nc-reply':
        case '/main/laboratory/form73-calibration':
        case '/main/laboratory/advisory':  
        case '/main/laboratoryNC/nc-reply':
        case '/main/laboratory-search':
        case '/main/ilacReport':
        case '/main/upfront-fees':
        
        return <DashboardHeader />;
      case '/main/change-password':
      case '/main/forgot-password':
      case '/main/reset-password':
        return <LoginHeader />; // Use login header for password-related pages
      case '/main/location':
        return null; // No header for location page
      case '/main/medical/user-manual-viewer':
        return null; // No header for PDF viewer page
      case '/main/testing/user-manual-viewer':
        return null; // No header for PDF viewer page
      case '/main/preview-report':
        return null; // No header for preview report page
      case '/main/preview-report-additional':
        return null; // No header for preview report page
      case '/main/preview-report-ppp-ffl':
        return null; // No header for preview report page
      case '/main/testing-preview-report':
        return null; // No header for testing preview report page
      case '/main/corruption-declaration':
      case '/main/termsandconditions':
      case '/main/termsandConditionsview':
      case '/main/al-outstanding-ledger':
      case '/main/declaration':
      case '/main/assessment-report':
        return null;
      case '/main/assessment-report-calibration':
        return null; // No header for declaration page
      case '/main/assessment-report-testing':
        return null; // No header for declaration page
      case '/main/view-report':
        return null; // No header for preview report page
      case '/main/viewCertificate':
      case '/main/downloadsymbol':
      case '/main/ilac-mark-download':
        return null;
      case '/main/ac-process':
      case '/main/advisory':
        return null;  
      case '/main/view-scope-master':
      case '/main/view-equipment-master':
        return null;
      case '/main/add-on/change-name':
        return null;
      case '/main/add-on/acquisition-merger':
        return null;
      case '/main/add-on/change-incab-permises':
        return null; 
      case '/main/add-on/ilac':
      case '/main/nabl-support/dashboard':
        return null;    
        default:
        return <HomeHeader />;
    }
  };

  return (
    <ThemeProvider theme={theme}>
      {useAutoLogout()}
      <Box 
        sx={{ 
          minHeight: '100vh',
        }}
      >
        {renderHeader()}
        <AppRouter />
        
        {/* Session timeout warning dialog */}
        <SessionTimeoutWarning />
        
        {/* Toast notifications */}
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
      </Box>
    </ThemeProvider>
  );
}

export default App;
